import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useToast } from '@zillow/constellation';
import {
  Box,
  Button,
  Combobox,
  DateInput,
  DatePicker,
  Divider,
  DropdownSelect,
  Heading,
  Icon,
  Modal,
  Spinner,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import type { ComboboxInputPropsInterface, ComboboxOptionType } from '@zillow/constellation-10';
import { IconCalendarFilled, IconClockFilled } from '@zillow/constellation-icons';
import { useQueryClient } from '@zg-rentals/react-query5';
import { ZCalendarWebApiHandler_getAvailableTimesForRescheduleByIrene_Path } from '@zg-rentals/ts-z-calendar-client';
import {
  useTourSchedulingWebApiHandler_rescheduleTourByIrene,
  useZCalendarWebApiHandler_getAvailableTimesForRescheduleByIrene,
} from '@zg-rentals/ts-z-calendar-client-react-query';

import EventDetails from '../EventDetails';
import UnmaskedToast from '../UnmaskedToast';
import type { RescheduleTourModalTriggers } from '../../constants';
import {
  type ClickStreamUser,
  emitRescheduleTourModalImpresssionEvent,
} from '../../constants/TouringClickStreamEvents';
import {
  convert12HourTimeToLocalTime,
  convertDateToLocalDate,
  convertLocalDateTimeToDate,
  formatTourTimeRange,
  getToastDisplayDate,
  getTourDateAndTimeRange,
} from '../../utils/formatTime';

type RescheduleTourModalProps = {
  clickStreamUser?: ClickStreamUser;
  listingAlias: string;
  tourId: string;
  tourTitle: string;
  triggerSource: RescheduleTourModalTriggers;
  onClose: () => void;
  onRescheduleSuccess?: (startMs?: number) => void;
};

const DATE_FORMAT = 'EEE, LLL d yyyy';

const formatDateString = (date: Date) => {
  return format(date, DATE_FORMAT);
};

const getStartTimeFromTimeSlot = (timeSlot: string): string => {
  const [startTime, endTime] = timeSlot.split('-');

  if (startTime.includes('am') || startTime.includes('pm')) {
    return startTime;
  }

  const meridiem = endTime.slice(-2);
  return `${startTime}${meridiem}`;
};

const RescheduleTourModal = ({
  clickStreamUser,
  listingAlias,
  tourId,
  tourTitle,
  triggerSource,
  onClose,
  onRescheduleSuccess,
}: RescheduleTourModalProps) => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTimeSlot, setRescheduleTimeSlot] = useState('');
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (clickStreamUser && triggerSource) {
      emitRescheduleTourModalImpresssionEvent(clickStreamUser, triggerSource);
    }
  }, [clickStreamUser, triggerSource]);

  const {
    data: getRescheduleTimesRes,
    isLoading: isGetRescheduleTimesLoading,
    isSuccess: isGetRescheduleTimesSuccess,
  } = useZCalendarWebApiHandler_getAvailableTimesForRescheduleByIrene({
    listingAlias,
    tourId,
  });

  const {
    mutateAsync: rescheduleTour,
    isPending: isRescheduleTourLoading,
    isSuccess: isRescheduleTourSuccess,
  } = useTourSchedulingWebApiHandler_rescheduleTourByIrene();

  const datePickerInputValue = useMemo(() => {
    if (!rescheduleDate) {
      return rescheduleDate;
    }
    return format(rescheduleDate, DATE_FORMAT);
  }, [rescheduleDate]);

  const rescheduleTimeSlotsByDate = useMemo(() => {
    if (!getRescheduleTimesRes?.availableTimeSlotsByDate) {
      return null;
    }

    const timeSlotsByDate = Object.values(getRescheduleTimesRes.availableTimeSlotsByDate).reduce<
      Record<string, Array<string>>
    >((acc, availableTimeSlots) => {
      availableTimeSlots.forEach((availableTimeSlot) => {
        const dateKey = formatDateString(convertLocalDateTimeToDate(availableTimeSlot.startTime));
        const formattedTourInterval = formatTourTimeRange(availableTimeSlot.startTime, availableTimeSlot.endTime);

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(formattedTourInterval);
      });
      return acc;
    }, {});

    return timeSlotsByDate;
  }, [getRescheduleTimesRes?.availableTimeSlotsByDate]);

  useEffect(() => {
    if (isGetRescheduleTimesSuccess && getRescheduleTimesRes) {
      const { startDateTime, endDateTime } = getRescheduleTimesRes;
      const tourDate = formatDateString(convertLocalDateTimeToDate(startDateTime));
      const tourTimeSlot = formatTourTimeRange(startDateTime, endDateTime);
      setRescheduleDate(tourDate);
      setRescheduleTimeSlot(tourTimeSlot);
    }
  }, [isGetRescheduleTimesSuccess, getRescheduleTimesRes]);

  useEffect(() => {
    if (isRescheduleTourSuccess) {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === ZCalendarWebApiHandler_getAvailableTimesForRescheduleByIrene_Path,
      });
    }
  }, [isRescheduleTourSuccess, queryClient]);

  const handleChangeDate = (value: string | Date) => {
    setRescheduleDate(value.toString());
    setRescheduleTimeSlot('');
  };

  const isDateDisabled = (date: Date) => {
    if (!rescheduleTimeSlotsByDate) {
      return true;
    }
    const dateKey = formatDateString(date);
    return !(dateKey in rescheduleTimeSlotsByDate);
  };

  const handleSelectTime = (option: ComboboxOptionType) => {
    setRescheduleTimeSlot(option as string);
  };

  const isRescheduleButtonDisabled = () => {
    if (
      !rescheduleDate ||
      !rescheduleTimeSlot ||
      isRescheduleTourLoading ||
      isGetRescheduleTimesLoading ||
      !getRescheduleTimesRes
    ) {
      return true;
    }

    const { startDateTime, endDateTime } = getRescheduleTimesRes;
    const originalTourDate = formatDateString(convertLocalDateTimeToDate(startDateTime));
    const originalTourTimeSlot = formatTourTimeRange(startDateTime, endDateTime);

    const isDateUnchanged = rescheduleDate === originalTourDate;
    const isTimeUnchanged = rescheduleTimeSlot === originalTourTimeSlot;
    return isDateUnchanged && isTimeUnchanged;
  };

  const handleRescheduleTour = () => {
    const startTimeString = getStartTimeFromTimeSlot(rescheduleTimeSlot);
    const localDate = convertDateToLocalDate(new Date(rescheduleDate));
    const localTime = convert12HourTimeToLocalTime(startTimeString);
    const startTime = { date: localDate, time: localTime };

    return rescheduleTour({
      postData: {
        listingAlias,
        tourId,
        startTime,
      },
    })
      .then((data) => {
        const tourDateAndTime = getToastDisplayDate(convertLocalDateTimeToDate(startTime));
        const successMessage = `The tour was rescheduled to ${tourDateAndTime}.`;
        enqueueToast(<UnmaskedToast appearance="success">{successMessage}</UnmaskedToast>);
        return onRescheduleSuccess?.(data.startMs);
      })
      .catch(() => {
        enqueueToast(
          <UnmaskedToast appearance="critical">
            The tour couldn&apos;t be rescheduled. Try again in a few moments.
          </UnmaskedToast>,
        );
      })
      .finally(() => {
        onClose();
      });
  };

  const modalBody =
    isGetRescheduleTimesLoading || !rescheduleTimeSlotsByDate || !getRescheduleTimesRes || !datePickerInputValue ? (
      <Box css={{ display: 'flex', justifyContent: 'center', padding: 'looser' }}>
        <Spinner size="xl" />
      </Box>
    ) : (
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        <EventDetails
          bookedByName={getRescheduleTimesRes.bookedByName}
          eventDateAndTime={getTourDateAndTimeRange(
            getRescheduleTimesRes.startDateTime,
            getRescheduleTimesRes.endDateTime,
          )}
          listingStreet={getRescheduleTimesRes.listingStreet || ''}
          photoUrl={getRescheduleTimesRes?.listingPhoto?.displayableMediumUrl}
          title={tourTitle}
          tourStatus={getRescheduleTimesRes.tourStatus}
        />
        <Divider />
        <Text>
          Pick a new date/time to reschedule. This will book the tour, so make sure you&apos;ve confirmed this time
          works for the renter.
        </Text>
        <DatePicker
          closeModalOnDateSelect
          dateFormat={DATE_FORMAT}
          disabledDates={isDateDisabled}
          onOpenChange={(open) => setIsCalendarOpen(open)}
          open={isCalendarOpen}
          input={
            <DateInput
              css={{ paddingLeft: 'sm' }}
              onClick={() => setIsCalendarOpen(true)}
              onKeyDown={(event) => {
                if (event.code === 'Enter' || event.code === 'Space') {
                  setIsCalendarOpen(true);
                }
              }}
              readOnly
              value={format(datePickerInputValue, 'EEE, LLL d')}
            />
          }
          onValueChange={handleChangeDate}
          trigger={
            <TextButton css={{ paddingRight: 'default' }} tone="neutral">
              <Icon size="sm" render={<IconCalendarFilled />} />
            </TextButton>
          }
          value={datePickerInputValue}
        />
        <DropdownSelect
          options={rescheduleTimeSlotsByDate[rescheduleDate] || []}
          onOptionSelect={handleSelectTime}
          placeholder="Select a time"
          renderAdornment={(_adornmentProps) => <Icon size="sm" render={<IconClockFilled />} />}
          renderInput={(renderProps: ComboboxInputPropsInterface) => {
            const { ref, ...adornmentPropsWithoutRef } = renderProps;
            return <Combobox.Input {...adornmentPropsWithoutRef} data-testid="RescheduleTourModal-TimeSlotDropdown" />;
          }}
          value={rescheduleTimeSlot}
        />
      </Box>
    );

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={<Heading level={3}>Reschedule tour</Heading>}
      body={modalBody}
      footer={
        <Button
          data-testid="RescheduleTourModal-RescheduleBtn"
          disabled={isRescheduleButtonDisabled()}
          onClick={handleRescheduleTour}
          emphasis="filled"
          fluid
        >
          Reschedule tour
        </Button>
      }
      size="xs"
    />
  );
};

export default RescheduleTourModal;
